import React, { useState } from 'react';
import { Wand2 } from 'lucide-react';

type Sport = 'football' | 'basketball' | 'soccer' | 'baseball' | 'hockey';
type TeamStyle = 'professional' | 'local' | 'youth' | 'college';

const sportPrefixes = {
  football: ['Mighty', 'Iron', 'Thunder', 'Royal', 'Savage', 'Crimson', 'Golden', 'Shadow'],
  basketball: ['Flying', 'Swift', 'Power', 'Elite', 'Rapid', 'Cosmic', 'Urban', 'Rising'],
  soccer: ['United', 'Real', 'Inter', 'Dynamic', 'Metro', 'Global', 'City', 'Athletic'],
  baseball: ['Classic', 'Grand', 'Prime', 'Legacy', 'Diamond', 'Capital', 'Crown', 'Empire'],
  hockey: ['Ice', 'Frost', 'Arctic', 'Polar', 'Blizzard', 'Northern', 'Winter', 'Frozen']
};

const styleSuffixes = {
  professional: ['Athletics', 'United', 'Elite', 'Force', 'Alliance', 'Stars', 'Pros', 'Champions'],
  local: ['Heroes', 'Legends', 'Squad', 'Crew', 'Team', 'Warriors', 'Titans', 'Knights'],
  youth: ['Cubs', 'Juniors', 'Academy', 'Rising', 'Future', 'Youth', 'Next', 'Development'],
  college: ['University', 'College', 'Campus', 'Academic', 'Institute', 'Tech', 'State', 'Central']
};

const generateNames = (sport: Sport, style: TeamStyle, keywords: string): string[] => {
  const prefixes = sportPrefixes[sport];
  const suffixes = styleSuffixes[style];
  
  const names: string[] = [];
  for (let i = 0; i < 5; i++) {
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
    const keyword = keywords ? ` ${keywords} ` : ' ';
    names.push(`${prefix}${keyword}${suffix}`);
  }
  
  return names;
};

const SportsTeamNameGenerator: React.FC = () => {
  const [sport, setSport] = useState<Sport>('football');
  const [style, setStyle] = useState<TeamStyle>('professional');
  const [keywords, setKeywords] = useState('');
  const [generatedNames, setGeneratedNames] = useState<string[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);

  const handleGenerate = () => {
    const names = generateNames(sport, style, keywords);
    setGeneratedNames(names);
  };

  const toggleFavorite = (name: string) => {
    setFavorites(prev => 
      prev.includes(name) 
        ? prev.filter(n => n !== name)
        : [...prev, name]
    );
  };

  return (
    <section id="sports-team-generator" className="py-20 bg-gray-50 scroll-mt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="bg-indigo-100 text-indigo-800 px-4 py-1 rounded-full text-sm font-medium">
            AI-Powered Team Names
          </span>
        </div>
        
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Sports Team Name Generator
        </h2>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sport
              </label>
              <select
                value={sport}
                onChange={(e) => setSport(e.target.value as Sport)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                <option value="football">Football</option>
                <option value="basketball">Basketball</option>
                <option value="soccer">Soccer</option>
                <option value="baseball">Baseball</option>
                <option value="hockey">Hockey</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team Level
              </label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value as TeamStyle)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                <option value="professional">Professional</option>
                <option value="local">Local/Amateur</option>
                <option value="youth">Youth/Development</option>
                <option value="college">College/University</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Keywords (Optional)
              </label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="E.g., Lions, Eagles, Storm"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white"
              />
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={handleGenerate}
              className="bg-indigo-600 text-white py-4 px-8 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
            >
              <Wand2 className="w-5 h-5" />
              Generate Names
            </button>
          </div>

          {generatedNames.length > 0 && (
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Generated Names:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {generatedNames.map((name, index) => (
                  <div
                    key={index}
                    className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between"
                  >
                    <span className="text-lg">{name}</span>
                    <button
                      onClick={() => toggleFavorite(name)}
                      className={`ml-4 p-2 rounded-full transition-colors ${
                        favorites.includes(name)
                          ? 'text-yellow-500 hover:text-yellow-600'
                          : 'text-gray-400 hover:text-gray-500'
                      }`}
                    >
                      ★
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 text-center text-sm text-gray-500">
            <p className="mb-2">Pro tip: Click the star icon to save your favorite names!</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SportsTeamNameGenerator;